<script setup lang="ts">
import { computed } from "vue";
import EditableElement from "@/views/page/EditableElement.vue";
import type { Stats3Props } from "./stats3Props";
import { PageBlock } from "@/views/blocks/PageBlock.interface";
import type { CssVarColors } from "@/shared/themes/CssVarColors.interface";
import Section from "@/views/blocks/shared/section/layout/section/Section.vue";
import FullSpacing from "@/views/blocks/shared/section/layout/spacing/FullSpacing.vue";
import Heading from "@/views/blocks/shared/section/typography/heading/Heading.vue";
import Paragraph from "@/views/blocks/shared/section/typography/paragraph/Paragraph.vue";

const props = defineProps<{
    block: PageBlock<Stats3Props>;
    pageColors: CssVarColors;
}>();

const dataValues = computed(() => props.block.dataValues);
const styleValues = computed(() => props.block.styleValues);
</script>

<template>
    <Section :id="block.webId" :background-color="styleValues.background">
        <FullSpacing>
            <div class="mx-auto max-w-7xl">
                <div class="mx-auto max-w-2xl lg:mx-0 lg:max-w-none">
                    <EditableElement
                        v-if="!dataValues.title.isHidden"
                        :block-id="block.id"
                        field-name="title"
                    >
                        <Heading
                            :text="dataValues.title.data.text"
                            :tag="dataValues.title.data.tag"
                            :text-color="styleValues.text"
                            class="text-4xl sm:text-5xl"
                        />
                    </EditableElement>
                    <div class="mt-6 flex flex-col gap-x-8 gap-y-20 lg:flex-row">
                        <div class="lg:w-full lg:max-w-2xl lg:flex-auto">
                            <EditableElement
                                v-if="!dataValues.subtitle.isHidden"
                                :block-id="block.id"
                                field-name="subtitle"
                            >
                                <Paragraph
                                    :text="dataValues.subtitle.data.text"
                                    :text-color="styleValues.text"
                                    class="text-xl/8 opacity-70"
                                />
                            </EditableElement>
                        </div>
                        <div class="lg:flex lg:flex-auto lg:justify-center">
                            <dl class="w-64 space-y-8 xl:w-80">
                                <div
                                    class="flex flex-col-reverse gap-y-4"
                                    v-for="stat in dataValues.stats.data.items"
                                    :key="`stat${stat.id}`"
                                    :style="{
                                        color: styleValues.text,
                                    }"
                                >
                                    <EditableElement
                                        :block-id="block.id"
                                        :field-name="`stats_${stat.id}`"
                                        :hideable="false"
                                    >
                                        <dt class="text-base/7 opacity-70">
                                            {{ stat.statDesc }}
                                        </dt>
                                        <dd class="text-5xl font-semibold tracking-tight">
                                            {{ stat.statTitle }}
                                        </dd>
                                    </EditableElement>
                                </div>
                            </dl>
                        </div>
                    </div>
                </div>
            </div>
        </FullSpacing>
    </Section>
</template>
